<template>
  <header class="h-16 bg-dark-800 border-b border-dark-700 px-6 flex items-center justify-between sticky top-0 z-30">
    <!-- Left: Breadcrumb -->
    <div class="flex items-center gap-4">
      <nav class="flex items-center gap-2 text-sm">
        <RouterLink to="/" class="text-dark-400 hover:text-dark-100 transition-colors">
          <HomeIcon class="w-4 h-4" />
        </RouterLink>
        <template v-if="breadcrumbs.length > 0">
          <ChevronRightIcon class="w-4 h-4 text-dark-600" />
          <template v-for="(crumb, index) in breadcrumbs" :key="crumb.path">
            <RouterLink
              v-if="index < breadcrumbs.length - 1"
              :to="crumb.path"
              class="text-dark-400 hover:text-dark-100 transition-colors"
            >
              {{ crumb.label }}
            </RouterLink>
            <span v-else class="text-dark-100 font-medium">{{ crumb.label }}</span>
            <ChevronRightIcon 
              v-if="index < breadcrumbs.length - 1" 
              class="w-4 h-4 text-dark-600" 
            />
          </template>
        </template>
      </nav>
    </div>

    <!-- Center: Search -->
    <div class="flex-1 max-w-xl mx-8">
      <div class="relative">
        <MagnifyingGlassIcon class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-500" />
        <input
          type="text"
          placeholder="Buscar zonas, registros, IPs... (⌘K)"
          class="w-full pl-10 pr-4 py-2 bg-dark-900 border border-dark-700 rounded-lg text-dark-200 placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          @click="openCommandPalette"
          readonly
        />
        <kbd class="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 bg-dark-700 text-dark-400 text-xs rounded">
          ⌘K
        </kbd>
      </div>
    </div>

    <!-- Right: Actions -->
    <div class="flex items-center gap-3">
      <!-- Connection Status -->
      <div class="flex items-center gap-2 px-3 py-1.5 bg-dark-900 rounded-lg">
        <span 
          class="status-dot"
          :class="wsConnected ? 'status-online' : 'status-offline'"
        />
        <span class="text-xs text-dark-400">
          {{ wsConnected ? 'Conectado' : 'Desconectado' }}
        </span>
      </div>

      <!-- Refresh Button -->
      <button
        class="btn btn-ghost btn-icon"
        :class="{ 'animate-spin-slow': refreshing }"
        @click="handleRefresh"
        :disabled="refreshing"
      >
        <ArrowPathIcon class="w-5 h-5" />
      </button>

      <!-- Notifications -->
      <div class="relative">
        <button
          class="btn btn-ghost btn-icon relative"
          @click="showNotifications = !showNotifications"
        >
          <BellIcon class="w-5 h-5" />
          <span
            v-if="unreadCount > 0"
            class="absolute -top-1 -right-1 w-5 h-5 bg-danger-500 text-white text-xs rounded-full flex items-center justify-center"
          >
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </button>

        <!-- Notifications Dropdown -->
        <Transition name="dropdown">
          <div
            v-if="showNotifications"
            class="dropdown right-0 w-80"
          >
            <div class="px-4 py-3 border-b border-dark-700">
              <h3 class="font-semibold text-dark-100">Notificações</h3>
            </div>
            <div class="max-h-64 overflow-y-auto">
              <div
                v-for="notification in recentNotifications"
                :key="notification.id"
                class="dropdown-item"
              >
                <div
                  class="w-2 h-2 rounded-full flex-shrink-0"
                  :class="{
                    'bg-success-500': notification.type === 'success',
                    'bg-warning-500': notification.type === 'warning',
                    'bg-danger-500': notification.type === 'error',
                    'bg-primary-500': notification.type === 'info',
                  }"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-sm text-dark-200 truncate">{{ notification.message }}</p>
                  <p class="text-xs text-dark-500">{{ notification.time }}</p>
                </div>
              </div>
              <div v-if="recentNotifications.length === 0" class="px-4 py-6 text-center text-dark-500 text-sm">
                Nenhuma notificação
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Theme Toggle -->
      <button
        class="btn btn-ghost btn-icon"
        @click="themeStore.toggleTheme()"
      >
        <SunIcon v-if="themeStore.isDark" class="w-5 h-5" />
        <MoonIcon v-else class="w-5 h-5" />
      </button>

      <!-- User Menu -->
      <div class="relative">
        <button
          class="flex items-center gap-3 px-3 py-1.5 rounded-lg hover:bg-dark-700 transition-colors"
          @click="showUserMenu = !showUserMenu"
        >
          <div class="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
            <span class="text-white text-sm font-medium">{{ userInitials }}</span>
          </div>
          <div v-if="authStore.user" class="text-left hidden sm:block">
            <p class="text-sm font-medium text-dark-100">{{ authStore.user.username }}</p>
            <p class="text-xs text-dark-500 capitalize">{{ authStore.user.role }}</p>
          </div>
          <ChevronDownIcon class="w-4 h-4 text-dark-500" />
        </button>

        <!-- User Dropdown -->
        <Transition name="dropdown">
          <div
            v-if="showUserMenu"
            class="dropdown right-0"
          >
            <RouterLink to="/settings" class="dropdown-item">
              <Cog6ToothIcon class="w-5 h-5 text-dark-400" />
              <span>Configurações</span>
            </RouterLink>
            <RouterLink to="/settings/api-keys" class="dropdown-item">
              <KeyIcon class="w-5 h-5 text-dark-400" />
              <span>Chaves API</span>
            </RouterLink>
            <div class="border-t border-dark-700 my-1" />
            <button class="dropdown-item w-full text-danger-400" @click="handleLogout">
              <ArrowRightOnRectangleIcon class="w-5 h-5" />
              <span>Sair</span>
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useDashboardStore } from '@/stores/dashboard'
import { wsConnection } from '@/services/api'
import {
  HomeIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  KeyIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/vue/24/outline'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const dashboardStore = useDashboardStore()

const showNotifications = ref(false)
const showUserMenu = ref(false)
const refreshing = ref(false)
const wsConnected = ref(false)

const breadcrumbs = computed(() => {
  const crumbs: Array<{ label: string; path: string }> = []
  
  if (route.meta.title) {
    crumbs.push({
      label: route.meta.title as string,
      path: route.path,
    })
  }
  
  return crumbs
})

const userInitials = computed(() => {
  if (!authStore.user?.username) return '?'
  return authStore.user.username.slice(0, 2).toUpperCase()
})

const unreadCount = ref(3)
const recentNotifications = ref([
  { id: 1, type: 'warning', message: 'Alta taxa de queries detectada em NS1', time: '5 min atrás' },
  { id: 2, type: 'success', message: 'Zona example.com sincronizada', time: '15 min atrás' },
  { id: 3, type: 'info', message: 'Backup automático concluído', time: '1 hora atrás' },
])

function openCommandPalette() {
  // Will be handled by CommandPalette component
  document.dispatchEvent(new CustomEvent('open-command-palette'))
}

async function handleRefresh() {
  refreshing.value = true
  await dashboardStore.fetchMetrics()
  setTimeout(() => {
    refreshing.value = false
  }, 1000)
}

async function handleLogout() {
  await authStore.logout()
  router.push('/login')
}

// Close dropdowns on click outside
function handleClickOutside(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (!target.closest('.relative')) {
    showNotifications.value = false
    showUserMenu.value = false
  }
}

// Check WebSocket connection status / API health
async function checkWsConnection() {
  try {
    // Check if API is responding
    const response = await fetch('/api/v1/health')
    wsConnected.value = response.ok
  } catch {
    wsConnected.value = wsConnection.isConnected
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  checkWsConnection()
  const interval = setInterval(checkWsConnection, 30000) // Check every 30 seconds
  onUnmounted(() => clearInterval(interval))
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
